import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { resolveMediaUrl } from "../lib/api";
import { toast } from "sonner";
import BulkIngest from "../components/BulkIngest";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("media");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bg mono text-ink-muted">// authenticating…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="px-3 md:px-4 h-16 flex items-center gap-3 hairline-b">
        <Link to="/" className="ab-logo shrink-0">AK</Link>
        <span className="pill pill-active pill-sm">ADMIN</span>
        <span className="label-overline hidden md:inline">// Console</span>
        <div className="flex-1" />
        <span className="mono text-[11px] text-ink-muted hidden sm:inline">{user.email}</span>
        <button onClick={logout} className="pill pill-sm" data-testid="admin-logout">Logout →</button>
      </header>

      <div className="px-3 md:px-4 py-4 flex items-center gap-2 hairline-b">
        <button onClick={() => setTab("media")} data-testid="tab-media" className={`pill ${tab === "media" ? "pill-active" : ""}`}>▦ Media</button>
        <button onClick={() => setTab("dossiers")} data-testid="tab-dossiers" className={`pill ${tab === "dossiers" ? "pill-active" : ""}`}>▣ Dossiers</button>
      </div>

      <main className="px-3 md:px-4 py-6 flex-1">
        {tab === "media" ? <MediaPanel /> : <DossiersPanel />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dossiers Panel
// ---------------------------------------------------------------------------
const DossiersPanel = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ slug: "", title: "", description: "", cover_image: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/dossiers").then((r) => setList(r.data || []));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/dossiers/${editing}`, form);
        toast.success("Dossier updated");
      } else {
        await api.post("/admin/dossiers", form);
        toast.success("Dossier created");
      }
      setForm({ slug: "", title: "", description: "", cover_image: "" });
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const edit = (d) => {
    setEditing(d.slug);
    setForm({ slug: d.slug, title: d.title, description: d.description || "", cover_image: d.cover_image || "" });
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete dossier "${slug}" and all its items?`)) return;
    await api.delete(`/admin/dossiers/${slug}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <form onSubmit={save} className="lg:col-span-5 hairline p-6 space-y-5" data-testid="dossier-form">
        <div className="flex items-center justify-between">
          <h2 className="heavy text-3xl">{editing ? "Edit dossier." : "New dossier."}</h2>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ slug: "", title: "", description: "", cover_image: "" }); }} className="pill pill-sm">Cancel</button>}
        </div>
        <Input label="Slug" testid="dossier-slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="vintage-maps" />
        <Input label="Title" testid="dossier-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Vintage Maps" />
        <Textarea label="Description" testid="dossier-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
        <Input label="Cover image url" testid="dossier-cover" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} placeholder="https://…" />
        <button disabled={busy} type="submit" data-testid="dossier-save" className="pill pill-active w-full justify-center">
          {editing ? "Update →" : "Create dossier →"}
        </button>
      </form>

      <div className="lg:col-span-7 space-y-3">
        <div className="label-overline">// {list.length} dossiers</div>
        {list.length === 0 && <div className="hairline p-8 mono text-[12px] text-ink-muted">No dossiers yet.</div>}
        {list.map((d) => (
          <div key={d.id} className="hairline p-4 flex items-center gap-4" data-testid={`dossier-row-${d.slug}`}>
            <div className="w-16 h-16 tile shrink-0 !aspect-square">
              {d.cover_image ? (
                <img src={resolveMediaUrl(d.cover_image)} alt="" className="tile-media" />
              ) : (
                <div className="tile-placeholder !text-xl">{(d.title || d.slug).slice(0,2).toUpperCase()}</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.14em] text-ink-muted">/{d.slug}</div>
              <div className="display text-lg mt-0.5 truncate">{d.title}</div>
              <div className="mono text-[11px] text-ink-muted">{d.item_count || 0} items</div>
            </div>
            <button onClick={() => edit(d)} className="pill pill-sm" data-testid={`edit-${d.slug}`}>Edit</button>
            <button onClick={() => remove(d.slug)} className="pill pill-sm text-red-300 hover:!bg-red-500/20 hover:!text-red-200" data-testid={`delete-${d.slug}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Media Panel
// ---------------------------------------------------------------------------
const TYPES = ["image", "video", "pdf", "audio", "embed"];
const STATUSES = ["LIVE", "DECLASSIFIED", "ARCHIVED"];

const MediaPanel = () => {
  const [list, setList] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [form, setForm] = useState(blank());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [filterDossier, setFilterDossier] = useState("");
  const [filterQ, setFilterQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [moveTarget, setMoveTarget] = useState("");

  const refresh = () => {
    const params = { limit: 500 };
    if (filterDossier) params.dossier = filterDossier;
    if (filterQ) params.q = filterQ;
    api.get("/media", { params }).then((r) => setList(r.data || []));
  };
  useEffect(() => {
    refresh();
    api.get("/dossiers").then((r) => setDossiers(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterDossier, filterQ]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => (s.size === list.length ? new Set() : new Set(list.map((m) => m.id))));
  };
  const clearSel = () => setSelected(new Set());

  const bulkMove = async () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    const target = moveTarget || null;
    try {
      const { data } = await api.post("/admin/media/bulk-move", { media_ids: ids, dossier_slug: target });
      toast.success(`Moved ${data.moved} item${data.moved === 1 ? "" : "s"} → /${data.dossier_slug}`);
      clearSel();
      setMoveTarget("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} item${selected.size === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post("/admin/media/bulk-delete", { media_ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} item${data.deleted === 1 ? "" : "s"}`);
      clearSel();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), year: form.year ? parseInt(form.year) : null };
      if (editing) {
        await api.put(`/admin/media/${editing}`, payload);
        toast.success("Media updated");
      } else {
        await api.post("/admin/media", payload);
        toast.success("Media ingested");
      }
      setForm(blank());
      setEditing(null);
      setShowUrlForm(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const edit = (m) => {
    setEditing(m.id);
    setShowUrlForm(true);
    setForm({
      title: m.title,
      description: m.description || "",
      dossier_slug: m.dossier_slug,
      media_type: m.media_type,
      source_type: m.source_type,
      source_url: m.source_url,
      thumbnail_url: m.thumbnail_url || "",
      year: m.year || "",
      author: m.author || "",
      tags: (m.tags || []).join(", "),
      status: m.status,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const onFile = async (e, field = "source_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.url, source_type: "upload" }));
      toast.success("File uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const getRowThumb = (m) => {
    if (m.thumbnail_url) return m.thumbnail_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.thumbnail_url}` : m.thumbnail_url;
    if (m.media_type === "image") return m.source_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.source_url}` : m.source_url;
    if (m.media_type === "pdf") return `${process.env.REACT_APP_BACKEND_URL}/api/pdf-thumb/${m.id}`;
    return null;
  };

  return (
    <div className="space-y-6">
      {!editing && <BulkIngest dossiers={dossiers} onDone={refresh} />}

      {(showUrlForm || editing) && (
        <form onSubmit={save} className="hairline p-6 space-y-5" data-testid="media-form">
          <div className="flex items-center justify-between">
            <h2 className="heavy text-3xl">{editing ? "Edit media." : "Add by URL."}</h2>
            <button type="button" onClick={() => { setEditing(null); setForm(blank()); setShowUrlForm(false); }} className="pill pill-sm">Close</button>
          </div>
          <Input label="Title *" testid="media-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Textarea label="Description" testid="media-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Dossier" testid="media-dossier" value={form.dossier_slug} onChange={(v) => setForm({ ...form, dossier_slug: v })} options={[{ value: "", label: "— unsorted —" }, ...dossiers.map((d) => ({ value: d.slug, label: d.title }))]} />
            <Select label="Media type" testid="media-type" value={form.media_type} onChange={(v) => setForm({ ...form, media_type: v })} options={TYPES.map((t) => ({ value: t, label: t.toUpperCase() }))} />
          </div>
          <Select label="Status" testid="media-status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} />
          <div>
            <div className="label-overline mb-2">// Source URL *</div>
            <Input value={form.source_url} onChange={(v) => setForm({ ...form, source_url: v, source_type: "url" })} placeholder="https://…" testid="media-source-url" />
            <label className="pill pill-sm mt-3 inline-flex cursor-pointer" data-testid="media-upload-label">
              ⇪ Upload single file
              <input type="file" className="hidden" onChange={(e) => onFile(e, "source_url")} data-testid="media-upload-input" />
            </label>
            {uploading && <span className="ml-3 mono text-[10px]">Uploading…</span>}
          </div>
          <Input label="Thumbnail url (optional)" testid="media-thumb" value={form.thumbnail_url} onChange={(v) => setForm({ ...form, thumbnail_url: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Year" testid="media-year" value={form.year} onChange={(v) => setForm({ ...form, year: v.replace(/[^0-9]/g, "") })} placeholder="1985" />
            <Input label="Author" testid="media-author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
          </div>
          <Input label="Tags (comma-separated)" testid="media-tags" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="rally, group b, 1985" />
          <button disabled={busy || !form.title || !form.source_url} type="submit" data-testid="media-save" className="pill pill-active w-full justify-center disabled:opacity-50">
            {editing ? "Update transmission" : "Ingest transmission →"}
          </button>
        </form>
      )}

      {!editing && !showUrlForm && (
        <div className="flex justify-end">
          <button onClick={() => setShowUrlForm(true)} data-testid="show-url-form" className="pill pill-sm">
            + Add by URL (YouTube / External)
          </button>
        </div>
      )}

      <div className="hairline p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={toggleAll}
            data-testid="select-all"
            className={`check ${selected.size > 0 && selected.size === list.length ? "check-on" : ""}`}
            aria-label="Select all"
            title={selected.size === list.length ? "Deselect all" : "Select all"}
          />
          <div className="label-overline">// {list.length} items</div>
          <span className="text-ink-faint mx-1">·</span>
          <select data-testid="list-filter-dossier" value={filterDossier} onChange={(e) => setFilterDossier(e.target.value)} className="input-mono w-auto">
            <option value="">All dossiers</option>
            {dossiers.map((d) => <option key={d.slug} value={d.slug}>{d.slug}</option>)}
          </select>
          <input
            data-testid="list-filter-q"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            placeholder="Filter by title, tag, author, PDF text…"
            className="input-mono flex-1 min-w-[200px]"
          />
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div
            data-testid="bulk-action-bar"
            className="mb-4 p-3 flex flex-wrap items-center gap-3 bg-yellow text-black rounded-full px-5"
          >
            <span className="mono text-[11px] font-semibold uppercase tracking-[0.14em]">
              {selected.size} selected
            </span>
            <span className="text-black/40">·</span>
            <select
              data-testid="bulk-move-target"
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              className="input-mono w-auto"
              style={{ background: "transparent", color: "#000", borderColor: "#000" }}
            >
              <option value="">→ Move to /unsorted</option>
              {dossiers.map((d) => <option key={d.slug} value={d.slug}>→ /{d.slug}</option>)}
            </select>
            <button
              onClick={bulkMove}
              data-testid="bulk-move-submit"
              className="pill pill-sm"
              style={{ background: "#000", color: "#fff10f", borderColor: "#000" }}
            >
              Move
            </button>
            <div className="flex-1" />
            <button
              onClick={bulkDelete}
              data-testid="bulk-delete-submit"
              className="pill pill-sm"
              style={{ background: "transparent", color: "#000", borderColor: "#000" }}
            >
              Delete
            </button>
            <button
              onClick={clearSel}
              data-testid="bulk-clear"
              className="mono text-[11px] uppercase tracking-[0.14em] underline underline-offset-2 hover:opacity-70"
            >
              Clear
            </button>
          </div>
        )}

        <div className="space-y-2">
          {list.length === 0 && <div className="mono text-[12px] text-ink-muted p-4">No media yet — drop files above to ingest.</div>}
          {list.map((m) => {
            const thumb = getRowThumb(m);
            const isSel = selected.has(m.id);
            return (
              <div
                key={m.id}
                className={`hairline p-3 flex items-center gap-4 ${isSel ? "bg-yellow/5" : ""}`}
                style={isSel ? { borderColor: "#fff10f" } : {}}
                data-testid={`media-row-${m.id}`}
              >
                <button
                  onClick={() => toggle(m.id)}
                  className={`check ${isSel ? "check-on" : ""}`}
                  data-testid={`select-${m.id}`}
                  aria-label={isSel ? "Deselect" : "Select"}
                />
                <div className="w-14 h-14 tile shrink-0 !aspect-square">
                  {thumb ? (
                    <img src={thumb} alt={m.title} className="tile-media" />
                  ) : (
                    <div className="tile-placeholder !text-sm">{m.media_type.toUpperCase()}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="pill pill-active pill-sm !py-1 !px-2 !text-[9px]">{m.ak_id}</span>
                    <span className="pill pill-sm !py-1 !px-2 !text-[9px]">{m.status}</span>
                    {m.ocr_text && (
                      <span
                        className="pill pill-sm !py-1 !px-2 !text-[9px]"
                        style={{ background: "#fff10f", color: "#000", borderColor: "#fff10f" }}
                        title="Full text extracted — searchable"
                      >⌕ OCR</span>
                    )}
                  </div>
                  <div className="text-[14px] truncate mt-1">{m.title}</div>
                  <div className="mono text-[10px] text-ink-muted">
                    {m.media_type} · {m.year || "—"} · {m.author || "—"} · /{m.dossier_slug}
                  </div>
                </div>
                <button onClick={() => edit(m)} className="pill pill-sm" data-testid={`media-edit-${m.id}`}>Edit</button>
                <button onClick={() => remove(m.id)} className="pill pill-sm text-red-300 hover:!bg-red-500/20 hover:!text-red-200" data-testid={`media-delete-${m.id}`}>Delete</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function blank() {
  return { title: "", description: "", dossier_slug: "", media_type: "image", source_type: "url", source_url: "", thumbnail_url: "", year: "", author: "", tags: "", status: "ARCHIVED" };
}

const Input = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    {label && <div className="label-overline mb-2">// {label}</div>}
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="input-mono"
    />
  </div>
);

const Textarea = ({ label, value, onChange, testid }) => (
  <div>
    <div className="label-overline mb-2">// {label}</div>
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="input-mono !rounded-2xl resize-y"
    />
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <div className="label-overline mb-2">// {label}</div>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="input-mono">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
